import { formatDateTimeWithZone } from "@/lib/utils"
import type {
  ProVenueInstance,
  ProVenueInstanceInput,
  AppleVendor,
  GoogleVendor,
  DisplayConfigurationItem,
  OperatingMode,
} from "@/types/alfred-config"
import { OPERATING_MODE_OPTIONS } from "@/types/alfred-config"

export interface AlfredDeviceConfig {
  id: number
  deviceId: string
  sotiDeviceId: string
  clientId: string
  venueCode: string
  inheritedFromGlobal: string[]
  validationVendor: string
  proVenueUser: string
  proVenueInstances: ProVenueInstance[]
  facilityCode: string
  league: string
  areaCode: string
  deviceCode: string
  offlineValidationMode: boolean
  offlineBatchUploadLimit: number
  soundValidNumber: number
  soundInvalidNumber: number
  soundWarningNumber: number
  maxCpuTemperature: number
  minCpuTemperature: number
  exitScanningAllowed: boolean
  adminModeIdleWindow: number
  validDisplayTime: number
  invalidDisplayTime: number
  operatingMode: OperatingMode
  turnstileMode: number
  unlockedAccessTime: number
  isLockFailSafe: boolean
  useSimpleResponse: boolean
  mainBackgroundColor: string
  secondaryBackgroundColor: string
  idleStateColor: string
  nfcEnabled: boolean
  enableFan: boolean
  enableHotspot: boolean
  wakeLockEnabled: boolean
  enableScanReset: boolean
  enableManualValidationMask: boolean
  displayConfiguration: DisplayConfigurationItem[]
  appleVendors: AppleVendor[]
  googleVendors: GoogleVendor[]
  ticketmasterVenueId: string
  createdAt: string
  updatedAt: string
  updatedBy: string
}

export interface AlfredDevicesResponse {
  items: AlfredDeviceConfig[]
  total: number
  page: number
  pageSize: number
}

export interface BulkUpdateAlfredDeviceFailure {
  id: number
  status: number
  code: string
  message: string
}

export interface BulkUpdateAlfredDevicesResult {
  requestedCount: number
  successCount: number
  failureCount: number
  failedDevices: BulkUpdateAlfredDeviceFailure[]
}

export type AlfredBulkDeleteStatus = "deleted" | "failed"

export interface BulkDeleteAlfredDeviceResult {
  id: number
  status: AlfredBulkDeleteStatus
  clientId?: string
  deviceId?: string
  error?: string
}

export interface BulkDeleteAlfredDevicesResult {
  results: BulkDeleteAlfredDeviceResult[]
  deleted: number
  failed: number
}

export const MAX_ALFRED_BULK_DEVICE_UPDATES = 200

export type BulkUpdateAlfredDeviceValue =
  | string
  | number
  | boolean
  | ProVenueInstanceInput[]
  | DisplayConfigurationItem[]
  | AppleVendor[]
  | GoogleVendor[]
  | null

export interface BulkUpdateAlfredDevicesRequest {
  ids: number[]
  update: Record<string, BulkUpdateAlfredDeviceValue>
}

export type SortOrder = "asc" | "desc"

// Mirrors ValidAlfredDeviceSortFields on the backend: the exact set of JSON field
// names the list endpoints accept for sortBy. Any other value returns HTTP 400, so
// this is the single source of truth for which columns the UI may offer sorting on.
// The array-valued fields (proVenueInstances, displayConfiguration, appleVendors,
// googleVendors), the write-only credential fields (configurationPassword,
// ticketmasterConsumerKey), the computed inheritedFromGlobal, and the primary
// key id are intentionally excluded.
export const ALFRED_DEVICE_SORT_FIELDS = [
  "deviceId",
  "sotiDeviceId",
  "createdAt",
  "clientId",
  "venueCode",
  "validationVendor",
  "proVenueUser",
  "facilityCode",
  "league",
  "areaCode",
  "deviceCode",
  "offlineValidationMode",
  "offlineBatchUploadLimit",
  "soundValidNumber",
  "soundInvalidNumber",
  "soundWarningNumber",
  "maxCpuTemperature",
  "minCpuTemperature",
  "exitScanningAllowed",
  "adminModeIdleWindow",
  "validDisplayTime",
  "invalidDisplayTime",
  "operatingMode",
  "turnstileMode",
  "unlockedAccessTime",
  "isLockFailSafe",
  "useSimpleResponse",
  "mainBackgroundColor",
  "secondaryBackgroundColor",
  "idleStateColor",
  "nfcEnabled",
  "enableFan",
  "enableHotspot",
  "wakeLockEnabled",
  "enableScanReset",
  "enableManualValidationMask",
  "ticketmasterVenueId",
  "updatedAt",
  "updatedBy",
] as const

export type AlfredDeviceSortField = (typeof ALFRED_DEVICE_SORT_FIELDS)[number]

const ALFRED_DEVICE_SORT_FIELD_SET: ReadonlySet<string> = new Set(ALFRED_DEVICE_SORT_FIELDS)

export function isAlfredDeviceSortField(value: string): value is AlfredDeviceSortField {
  return ALFRED_DEVICE_SORT_FIELD_SET.has(value)
}

// Query params shared by both Alfred device list endpoints. Undefined fields are
// omitted from the request so the backend applies its defaults (page 1, pageSize 50,
// deviceId ascending).
export interface AlfredDeviceListParams {
  page?: number
  pageSize?: number
  filter?: string
  sortBy?: AlfredDeviceSortField
  sortOrder?: SortOrder
}

export interface CreateAlfredDeviceRequest {
  deviceId: string
  clientId: number  // API expects the numeric PK of the Alfred client
  sotiDeviceId: string
  configurationPassword?: string | null
  venueCode?: string
  validationVendor?: string
  proVenueUser?: string
  proVenueInstances?: ProVenueInstanceInput[]
  facilityCode?: string
  league?: string
  areaCode?: string
  deviceCode?: string
  offlineValidationMode?: boolean
  offlineBatchUploadLimit?: number
  soundValidNumber?: number
  soundInvalidNumber?: number
  soundWarningNumber?: number
  maxCpuTemperature?: number
  minCpuTemperature?: number
  exitScanningAllowed?: boolean
  adminModeIdleWindow?: number
  validDisplayTime?: number
  invalidDisplayTime?: number
  operatingMode?: OperatingMode
  turnstileMode?: number
  unlockedAccessTime?: number
  isLockFailSafe?: boolean
  useSimpleResponse?: boolean
  mainBackgroundColor?: string
  secondaryBackgroundColor?: string
  idleStateColor?: string
  nfcEnabled?: boolean
  enableFan?: boolean
  enableHotspot?: boolean
  wakeLockEnabled?: boolean
  enableScanReset?: boolean
  enableManualValidationMask?: boolean
  displayConfiguration?: DisplayConfigurationItem[]
  appleVendors?: AppleVendor[]
  googleVendors?: GoogleVendor[]
  ticketmasterVenueId?: string
  ticketmasterConsumerKey?: string
}

export type CloneField =
  | "configurationPassword"
  | "proVenueInstances"
  | "ticketmasterConsumerKey"
  | "appleVendors"
  | "googleVendors"

export interface CloneAlfredDeviceRequest extends CreateAlfredDeviceRequest {
  cloneSensitiveFields: CloneField[]
}

// PUT replaces the full device config; null on a field tells the backend to
// clear the device-level override and fall back to the global default.
// The write-only credential fields use special shapes: configurationPassword
// and ticketmasterConsumerKey signal useDefault independently of the value
// (blank value + useDefault:false means "keep current"), and proVenueInstances
// accepts the full credential input shape (blank secrets on an existing
// instance mean "keep current"; null clears the override to inherit global).
export type UpdateAlfredDeviceRequest = {
  [K in keyof Omit<
    AlfredDeviceConfig,
    "id" | "createdAt" | "updatedAt" | "updatedBy" | "inheritedFromGlobal" | "proVenueInstances"
  >]?: AlfredDeviceConfig[K] | null
} & {
  configurationPassword?: { value: string | null; useDefault: boolean }
  ticketmasterConsumerKey?: { value: string | null; useDefault: boolean }
  proVenueInstances?: ProVenueInstanceInput[] | null
}

export interface AlfredDeviceUI {
  id: number
  deviceId: string
  sotiDeviceId: string
  clientId: string
  inheritedFromGlobal: Set<string>
  venueCode: string
  facilityCode: string
  league: string
  proVenueUser: string
  operatingMode: OperatingMode
  operatingModeLabel: string
  areaCode: string
  deviceCode: string
  validationVendor: string
  ticketmasterVenueId: string
  offlineValidationMode: boolean
  offlineBatchUploadLimit: number
  turnstileMode: number
  adminModeIdleWindow: number
  validDisplayTime: number
  invalidDisplayTime: number
  unlockedAccessTime: number
  soundValidNumber: number
  soundInvalidNumber: number
  soundWarningNumber: number
  maxCpuTemperature: number
  minCpuTemperature: number
  nfcEnabled: boolean
  exitScanningAllowed: boolean
  isLockFailSafe: boolean
  useSimpleResponse: boolean
  enableFan: boolean
  enableHotspot: boolean
  wakeLockEnabled: boolean
  enableScanReset: boolean
  enableManualValidationMask: boolean
  proVenueInstances: ProVenueInstance[]
  displayConfiguration: DisplayConfigurationItem[]
  appleVendors: AppleVendor[]
  googleVendors: GoogleVendor[]
  mainBackgroundColor: string
  secondaryBackgroundColor: string
  idleStateColor: string
  createdAt: string
  updatedAt: string
  updatedAtRaw: string
  updatedBy: string
}

export type AlfredDeviceConfigKey = keyof AlfredDeviceConfig

export function toAlfredDeviceUI(device: AlfredDeviceConfig): AlfredDeviceUI {
  const modeOption = OPERATING_MODE_OPTIONS.find((o) => o.value === device.operatingMode)
  return {
    id: device.id,
    deviceId: device.deviceId,
    sotiDeviceId: device.sotiDeviceId,
    clientId: device.clientId,
    inheritedFromGlobal: new Set(device.inheritedFromGlobal),
    venueCode: device.venueCode,
    facilityCode: device.facilityCode,
    league: device.league,
    proVenueUser: device.proVenueUser,
    operatingMode: device.operatingMode,
    operatingModeLabel: modeOption?.label ?? String(device.operatingMode),
    areaCode: device.areaCode,
    deviceCode: device.deviceCode,
    validationVendor: device.validationVendor,
    ticketmasterVenueId: device.ticketmasterVenueId,
    offlineValidationMode: device.offlineValidationMode,
    offlineBatchUploadLimit: device.offlineBatchUploadLimit,
    turnstileMode: device.turnstileMode,
    adminModeIdleWindow: device.adminModeIdleWindow,
    validDisplayTime: device.validDisplayTime,
    invalidDisplayTime: device.invalidDisplayTime,
    unlockedAccessTime: device.unlockedAccessTime,
    soundValidNumber: device.soundValidNumber,
    soundInvalidNumber: device.soundInvalidNumber,
    soundWarningNumber: device.soundWarningNumber,
    maxCpuTemperature: device.maxCpuTemperature,
    minCpuTemperature: device.minCpuTemperature,
    nfcEnabled: device.nfcEnabled,
    exitScanningAllowed: device.exitScanningAllowed,
    isLockFailSafe: device.isLockFailSafe,
    useSimpleResponse: device.useSimpleResponse,
    enableFan: device.enableFan,
    enableHotspot: device.enableHotspot,
    wakeLockEnabled: device.wakeLockEnabled,
    enableScanReset: device.enableScanReset,
    enableManualValidationMask: device.enableManualValidationMask,
    proVenueInstances: device.proVenueInstances,
    displayConfiguration: device.displayConfiguration,
    appleVendors: device.appleVendors,
    googleVendors: device.googleVendors,
    mainBackgroundColor: device.mainBackgroundColor,
    secondaryBackgroundColor: device.secondaryBackgroundColor,
    idleStateColor: device.idleStateColor,
    createdAt: formatDateTimeWithZone(device.createdAt),
    updatedAt: formatDateTimeWithZone(device.updatedAt),
    updatedAtRaw: device.updatedAt,
    updatedBy: device.updatedBy,
  }
}
